import { z } from 'zod';

import { userRoleWithSchoolSchema } from './user-role.js';
import { userSchema } from './user.js';
import { paymentAccountType } from './payment-account-type.js';
import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';

export const updateMeOptionsSchema = z.object({
  timeZone: z.string().min(1, { message: 'Time Zone is required' }),
  phone: z
    .string()
    .min(5, { message: 'Phone Number is too short' })
    .regex(/^\+\d+$/, {
      message: 'Phone Number must start with "+" and contain only digits',
    }),
});
export type UpdateMeOptions = z.infer<typeof updateMeOptionsSchema>;

export const getMeResultSchema = userSchema.extend({
  userRoles: z.array(userRoleWithSchoolSchema),
  zoomPersonalMeetingUrl: z.string().nullable(),
  zoomAccountId: z.string().nullable(),
});
export type GetMeResult = z.infer<typeof getMeResultSchema>;

export const updateMeCoachOptionsSchema = updateMeOptionsSchema.extend({
  firstName: z.string(),
  lastName: z.string(),
  smsReminderOptedIn: z.boolean(),
  secondaryEmail: z.string().refine((val) => !val || /@/.test(val), {
    message: 'Secondary Email must be a valid email address',
  }),
  currentTimeZone: z.string(),
  teachingPreferences: z.array(z.nativeEnum(PrismaEnums.TeachingPreference)),
});
export type UpdateMeCoachOptions = z.infer<typeof updateMeCoachOptionsSchema>;

export const getMeCoachResultSchema = getMeResultSchema.extend({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  smsReminderOptedIn: z.boolean(),
  secondaryEmail: z.string().nullable(),
  vendorId: z.string().nullable(),
  paymentInstrumentId: z.string().nullable(),
  paymentAccountType: paymentAccountType.nullable(),
  paymentAccountConnectedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  paymentAccountUpdatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  paymentAccountErrorAtInstant: z.instanceof(Temporal.Instant).nullable(),
  paymentAccountErrorMessage: z.string().nullable(),
  last4Ssn: z.string().nullable(),
  teachingPreferences: z.array(z.nativeEnum(PrismaEnums.TeachingPreference)),
  documentsReviewPassed: z.boolean(),
  documentsLastUploadedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  workshopPassed: z.boolean(),
  onboardingTechCheckPassed: z.boolean(),
  referralCode: z.string().nullable(),
});
export type GetMeCoachResult = z.infer<typeof getMeCoachResultSchema>;

export const coachOnboardingStepSchema = z.enum(['documents', 'workshop', 'techCheck']);
export type CoachOnboardingStep = z.infer<typeof coachOnboardingStepSchema>;
export const CoachOnboardingStep = coachOnboardingStepSchema.enum;

export const completeCoachOnboardingStepBodySchema = z.object({
  step: coachOnboardingStepSchema,
});
export type CompleteCoachOnboardingStepBody = z.infer<typeof completeCoachOnboardingStepBodySchema>;

/**
 * Roles supported by the test authentication endpoint (non-production only).
 * Used by POST /me/test-auth and automation tests.
 */
export const testAuthRoleSchema = z.enum(['admin', 'teacher', 'student']);
export type TestAuthRole = z.infer<typeof testAuthRoleSchema>;
export const TestAuthRole = testAuthRoleSchema.enum;

export const createTestAuthTokenOptionsSchema = z.union([
  z.object({ email: z.string() }),
  z.object({ role: testAuthRoleSchema }),
]);
export type CreateTestAuthTokenOptions = z.infer<typeof createTestAuthTokenOptionsSchema>;

export const testAuthTokenSchema = z.object({
  token: z.string(),
  email: z.string(),
  userId: z.string(),
  firebaseUid: z.string(),
});
export type TestAuthToken = z.infer<typeof testAuthTokenSchema>;
